import React, { useState, useEffect, useRef, KeyboardEvent, ChangeEvent, memo } from 'react';
import { X, Plus } from 'lucide-react';

interface TagInputProps {
  label: string;
  value: string[];
  onSave: (tags: string[]) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  maxTags?: number;
  suggestions?: string[];
}

// Comparateur personnalisé pour éviter les re-renders inutiles
const arePropsEqual = (prevProps: TagInputProps, nextProps: TagInputProps): boolean => {
  // Comparer les tableaux de valeurs par contenu
  const valuesEqual = 
    prevProps.value?.length === nextProps.value?.length &&
    prevProps.value?.every((v, i) => v === nextProps.value?.[i]);
  
  return (
    valuesEqual &&
    prevProps.label === nextProps.label &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.maxTags === nextProps.maxTags
    // onSave et suggestions ne sont pas comparés car ils changent à chaque render du parent
  );
};

const TagInputComponent: React.FC<TagInputProps> = ({
  label,
  value = [],
  onSave,
  disabled = false,
  placeholder = "Tapez et appuyez sur Entrée",
  maxTags,
  suggestions = []
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [tags, setTags] = useState<string[]>(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Ref pour stocker la valeur précédente et éviter les syncs inutiles
  const prevValueRef = useRef<string[]>(value);

  // Synchroniser les tags quand value change depuis le parent (comparaison profonde)
  useEffect(() => {
    // Vérifier si les valeurs ont vraiment changé
    const valuesChanged = 
      value.length !== prevValueRef.current.length ||
      value.some((v, i) => v !== prevValueRef.current[i]);
    
    if (valuesChanged) {
      setTags([...value]);
      prevValueRef.current = value;
    }
  }, [value]);

  // Focus sur l'input quand on passe en mode édition
  useEffect(() => {
    if (isEditing && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isEditing]);

  const handleStartEditing = () => {
    if (disabled) return;
    setIsEditing(true);
  };

  const handleFinishEditing = () => {
    setIsEditing(false);
    setInputValue('');
    setShowSuggestions(false);
  };

  const handleAddTag = async (tagToAdd: string) => {
    const trimmed = tagToAdd.trim();
    console.log('🏷️ handleAddTag appelé:', { tagToAdd: trimmed, currentTags: tags, disabled });
    if (!trimmed) return;
    if (tags.includes(trimmed)) {
      setInputValue('');
      return;
    }
    if (maxTags && tags.length >= maxTags) return;

    const newTags = [...tags, trimmed];
    console.log('🏷️ Nouveaux tags:', newTags);
    setTags(newTags);
    setInputValue('');
    setShowSuggestions(false);
    
    try {
      console.log('🏷️ Appel de onSave...');
      await onSave(newTags);
      console.log('🏷️ onSave terminé avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des tags:', error);
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const newTags = tags.filter(t => t !== tagToRemove);
    setTags(newTags);
    
    try {
      await onSave(newTags);
    } catch (error) {
      console.error('Erreur lors de la suppression du tag:', error);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleAddTag(inputValue);
    } else if (e.key === 'Escape') {
      handleFinishEditing();
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      handleRemoveTag(tags[tags.length - 1]);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(newValue.length > 0 && suggestions.length > 0);
  };

  const handleAddButtonClick = () => {
    if (inputValue.trim()) {
      handleAddTag(inputValue);
    }
  };

  const filteredSuggestions = suggestions.filter(
    suggestion =>
      suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
      !tags.includes(suggestion)
  ).slice(0, 5);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {maxTags && (
          <span className="text-xs text-gray-500 ml-1">
            ({tags.length}/{maxTags})
          </span>
        )}
      </label>

      {!isEditing ? (
        // Mode affichage - cliquer pour éditer
        <div
          onClick={handleStartEditing}
          className={`
            min-h-[2.5rem] p-2 border border-gray-300 rounded-lg
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50 hover:border-[#197ca8]'}
            transition-all duration-200
          `}
        >
          {tags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {tags.map((tag, index) => (
                <span
                  key={`${tag}-${index}`}
                  className="inline-flex items-center px-2 py-1 bg-[#197ca8] text-white text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-400 text-sm">
              {disabled ? 'Cliquez sur Éditer pour modifier' : 'Cliquez pour ajouter...'}
            </span>
          )}
        </div>
      ) : (
        // Mode édition
        <div className="space-y-2">
          <div className="border-2 border-[#197ca8] rounded-lg p-2 bg-white">
            {/* Tags existants */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {tags.map((tag, index) => (
                  <span
                    key={`${tag}-${index}`}
                    className="inline-flex items-center px-2 py-1 bg-[#197ca8] text-white text-xs rounded-full group"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveTag(tag);
                      }}
                      className="ml-1 hover:bg-[#156a92] rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Input avec bouton d'ajout */}
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowSuggestions(inputValue.length > 0 && suggestions.length > 0)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder={tags.length === 0 ? placeholder : "Ajouter..."}
                className="flex-1 border-none outline-none text-sm bg-transparent"
                disabled={maxTags ? tags.length >= maxTags : false}
              />
              {inputValue.trim() && (
                <button
                  type="button"
                  onClick={handleAddButtonClick}
                  className="p-1 bg-[#197ca8] text-white rounded hover:bg-[#156a92] transition-colors"
                  title="Ajouter le tag"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Suggestions */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className="border border-gray-200 rounded-lg bg-white shadow-lg max-h-32 overflow-y-auto z-10">
              {filteredSuggestions.map((suggestion, index) => (
                <button
                  key={`suggestion-${index}`}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleAddTag(suggestion);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-[#197ca8] hover:text-white transition-colors first:rounded-t-lg last:rounded-b-lg"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* Bouton Terminer */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleFinishEditing}
              className="px-4 py-1.5 text-sm bg-[#197ca8] text-white rounded-md hover:bg-[#156a92] transition-colors"
            >
              Terminer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Export mémorisé pour éviter les re-renders inutiles
export const TagInput = memo(TagInputComponent, arePropsEqual);